import { Download, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useBodyLock } from "../../hooks/useBodyLock";
import { Button } from "../ui";
import { toast } from "../../stores/toast";

function qrFilename(imageUrl: string): string {
  const match = imageUrl.match(/\.(png|jpe?g|webp|gif)(?:\?|$)/i);
  return `payment-qr.${match?.[1]?.toLowerCase() ?? "png"}`;
}

async function downloadQrImage(imageUrl: string) {
  try {
    const res = await fetch(imageUrl);
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = qrFilename(imageUrl);
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(imageUrl, "_blank", "noopener,noreferrer");
    toast("error", "Could not download — opened in a new tab instead");
  }
}

export function BankInQrModal({
  open,
  imageUrl,
  onClose,
}: {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
}) {
  useBodyLock(open);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="bank-in-qr-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center"
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: "spring", damping: 28, stiffness: 360 }}
            className="relative flex max-h-[min(90dvh,640px)] w-full max-w-sm flex-col overflow-hidden rounded-2xl border border-white/10 bg-surface shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bank-in-qr-title"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <div className="min-h-0 flex-1 overflow-y-auto p-5 pb-4">
              <h2 id="bank-in-qr-title" className="pr-8 font-display text-sm font-semibold text-white">
                Scan to pay
              </h2>
              <p className="mt-1 text-[11px] text-white/50">Use your banking app to scan this QR code.</p>

              <div className="mt-4 overflow-hidden rounded-xl border border-white/10 bg-white p-3">
                <img
                  src={imageUrl}
                  alt="Bank payment QR code"
                  className="mx-auto max-h-[min(50dvh,380px)] w-full object-contain"
                />
              </div>
            </div>

            <div className="flex shrink-0 gap-2 border-t border-white/5 p-4 pt-3">
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Close
              </Button>
              <Button className="flex-1" onClick={() => void downloadQrImage(imageUrl)}>
                <Download size={16} />
                Download
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
